"use client";

import { canUseMerchantPermission } from "@/modules/merchant/presentation/api/client";
import { usePosSaleDetailQuery } from "@/modules/merchant/presentation/api/queries";
import { printPosSaleReceipt } from "@/modules/merchant/presentation/lib/print-pos-sale-receipt";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { Button, Input, Label, confirm, toast } from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import {
  CopyIcon,
  ExternalLinkIcon,
  PrinterIcon,
  ShareIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import type { WorkshopJobDetail } from "../api/client";
import {
  useApproveWorkshopQuoteMutation,
  useWorkshopCheckoutMutation,
  useWorkshopQuoteQuery,
} from "../api/queries";
import { formatWorkshopMoney } from "../lib/workshop-labels";
import { printWorkshopQuote } from "../lib/print-workshop-quote";
import { workshopToastError } from "../lib/workshop-user-messages";

export function WorkshopQuoteCheckoutPanel({
  shopId,
  job,
  canEdit,
}: {
  shopId: string;
  job: WorkshopJobDetail;
  canEdit: boolean;
}) {
  const { currentPermissions, currentShop } = useMerchantShops(shopId);
  const canSell = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.workshop.sell,
    currentShop?.verifiedLevel ?? null,
  );
  const canPosSell = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.pos.sell,
    currentShop?.verifiedLevel ?? null,
  );
  const canPosRead = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.pos.read,
    currentShop?.verifiedLevel ?? null,
  );

  const quoteQ = useWorkshopQuoteQuery(shopId, job.id);
  const approveMut = useApproveWorkshopQuoteMutation(shopId);
  const checkoutMut = useWorkshopCheckoutMutation(shopId);

  const paid = job.status === "paid";
  const saleDetailQ = usePosSaleDetailQuery(
    shopId,
    job.posSaleId ?? undefined,
    Boolean(paid && job.posSaleId && canPosRead),
  );

  const [method, setMethod] = useState<"cash" | "transfer">("cash");
  const [transferRef, setTransferRef] = useState("");
  const idemKey = useMemo(
    () => `workshop:${job.id}:${crypto.randomUUID()}`,
    [job.id],
  );

  const quote = quoteQ.data;
  const approved = (quote?.quoteStatus ?? job.quoteStatus) === "approved";
  const canCheckout =
    canSell &&
    canPosSell &&
    job.status === "completed" &&
    approved &&
    !paid;

  const quotePrintContext = useMemo(
    () =>
      currentShop
        ? {
            shopName: currentShop.shopName,
            branchName: currentShop.branchName ?? null,
          }
        : undefined,
    [currentShop],
  );

  function printQuote(paper: "thermal80" | "a4") {
    if (!quote) return;
    if (
      (quote.partsCount === 0 && quote.laborsCount === 0) ||
      ((job.parts?.length ?? 0) === 0 && (job.labors?.length ?? 0) === 0)
    ) {
      toast.error("ຍັງບໍ່ມີລາຍການໃນໃບສະເໜີ");
      return;
    }
    printWorkshopQuote(job, quote, quotePrintContext, paper);
  }

  const receiptContextPaid = useMemo(
    () =>
      currentShop
        ? {
            shopName: currentShop.shopName,
            branchName:
              saleDetailQ.data?.branchName ?? currentShop.branchName ?? null,
          }
        : undefined,
    [currentShop, saleDetailQ.data?.branchName],
  );

  function printReceipt(paper: "thermal80" | "a4") {
    const detail = saleDetailQ.data;
    if (!detail) {
      toast.error("ກຳລັງໂຫຼດບິນ… ລອງໃໝ່");
      return;
    }
    printPosSaleReceipt(detail, receiptContextPaid, {
      paper,
      template: "simple",
    });
  }

  async function copyShare() {
    const text = quote?.shareText;
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("ຄັດລອກສະຫຼຸບແລ້ວ");
    } catch {
      toast.error("ຄັດລອກບໍ່ສຳເລັດ");
    }
  }

  async function shareNative() {
    const text = quote?.shareText;
    if (!text) return;
    if (navigator.share) {
      try {
        await navigator.share({ text, title: job.jobNumber });
      } catch {
        /* user cancel */
      }
    } else {
      await copyShare();
    }
  }

  async function approve() {
    if (!quote) return;
    const ok = await confirm({
      title: "ອະນຸມັດໃບສະເໜີລາຄາ",
      description: `ຢືນຢັນອະນຸມັດ ${job.jobNumber} · ${job.plateNumber} — ລວມ ${formatWorkshopMoney(quote.grandTotal, quote.currency)} ຫຼື ບໍ່?`,
      actionText: "ອະນຸມັດ",
    });
    if (!ok) return;
    try {
      await approveMut.mutateAsync(job.id);
      toast.success("ອະນຸມັດໃບສະເໜີລາຄາແລ້ວ");
    } catch (err) {
      toast.error(workshopToastError(err));
    }
  }

  async function checkout() {
    if (!quote) return;
    try {
      const result = await checkoutMut.mutateAsync({
        jobId: job.id,
        idempotencyKey: idemKey,
        body: {
          payments: [
            {
              method,
              amount: quote.grandTotal,
              reference:
                method === "transfer"
                  ? transferRef.trim() || undefined
                  : undefined,
            },
          ],
        },
      });
      toast.success(
        result.idempotentReplay
          ? `ບິນ #${result.saleNumber} (ຊ້ຳ)`
          : `ຊຳລະແລ້ວ · ບິນ #${result.saleNumber}`,
      );
    } catch (err) {
      toast.error(workshopToastError(err));
    }
  }

  if (quoteQ.isLoading) {
    return <p className="text-muted-foreground text-xs">ກຳລັງໂຫຼດສະຫຼຸບ…</p>;
  }

  if (quoteQ.isError) {
    return (
      <p className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-amber-950 text-xs">
        ຕ້ອງຕັ້ງອັດຕາແລກປ່ຽນ (THB/LAK) ໃນຕັ້ງຄ່າຮ້ານ ກ່ອນສະຫຼຸບຍອດໃບສະເໜີ
      </p>
    );
  }

  if (!quote) return null;

  return (
    <div className="space-y-3">
      <div className="space-y-1.5 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">ຄ່າອາໄຫຼ່</span>
          <span className="tabular-nums">
            {formatWorkshopMoney(quote.partsSubtotal, quote.currency)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">ຄ່າແຮງ</span>
          <span className="tabular-nums">
            {formatWorkshopMoney(quote.laborsSubtotal, quote.currency)}
          </span>
        </div>
        <div className="flex justify-between border-border border-t pt-2 font-bold text-base">
          <span>ລວມ</span>
          <span className="tabular-nums">
            {formatWorkshopMoney(quote.grandTotal, quote.currency)}
          </span>
        </div>
        {quote.taxTotal > 0 ? (
          <p className="text-muted-foreground text-xs">
            ລວມ VAT {quote.taxPercent}% ={" "}
            {formatWorkshopMoney(quote.taxTotal, quote.currency)}
          </p>
        ) : null}
        <p className="text-muted-foreground text-xs">
          ສະຖານະໃບສະເໜີ:{" "}
          {approved
            ? "ອະນຸມັດແລ້ວ"
            : quote.quoteStatus === "draft"
              ? "ຮ່າງ"
              : quote.quoteStatus}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={quote.partsCount === 0 && quote.laborsCount === 0}
          onClick={() => printQuote("thermal80")}
        >
          <PrinterIcon className="size-3.5" />
          ພິມ 80 mm
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={quote.partsCount === 0 && quote.laborsCount === 0}
          onClick={() => printQuote("a4")}
        >
          <PrinterIcon className="size-3.5" />
          ພິມ A4
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => void copyShare()}
        >
          <CopyIcon className="size-3.5" />
          ຄັດລອກ
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => void shareNative()}
        >
          <ShareIcon className="size-3.5" />
          ແຊຣ์
        </Button>
      </div>

      {canEdit && !paid && !approved ? (
        <Button
          type="button"
          className="h-11 w-full"
          disabled={
            approveMut.isPending ||
            (quote.partsCount === 0 && quote.laborsCount === 0)
          }
          onClick={() => void approve()}
        >
          ອະນຸມັດໃບສະເໜີລາຄາ
        </Button>
      ) : null}

      {paid && job.posSaleId ? (
        <div className="space-y-2 rounded-xl border border-emerald-200 bg-emerald-50/80 p-3">
          <p className="font-medium text-emerald-950 text-sm">
            ຈ່າຍແລ້ວ
            {saleDetailQ.data?.saleNumber
              ? ` · ບິນ #${saleDetailQ.data.saleNumber}`
              : null}
          </p>
          {canPosRead ? (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="bg-background"
                disabled={saleDetailQ.isLoading || !saleDetailQ.data}
                onClick={() => printReceipt("thermal80")}
              >
                <PrinterIcon className="size-3.5" />
                ພິມ 80 mm
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="bg-background"
                disabled={saleDetailQ.isLoading || !saleDetailQ.data}
                onClick={() => printReceipt("a4")}
              >
                <PrinterIcon className="size-3.5" />
                ພິມ A4
              </Button>
              <Link
                to="/app/shops/$shopId/pos/sales"
                params={{ shopId }}
                search={{
                  offset: 0,
                  limit: 20,
                  status: "paid",
                  createdFrom: undefined,
                  createdTo: undefined,
                  saleNumber: saleDetailQ.data?.saleNumber,
                  customerUserId: undefined,
                  customerSearch: undefined,
                }}
                className="inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-sm hover:bg-accent hover:text-accent-foreground"
              >
                <ExternalLinkIcon className="size-3.5" />
                ປະຫວັດຂາຍ
              </Link>
            </div>
          ) : (
            <p className="text-emerald-900 text-xs">
              ຜູກບິນ POS ແລ້ວ (ຕ້ອງມີສິດ pos:read ເພື່ອພິມ)
            </p>
          )}
        </div>
      ) : null}

      {canCheckout ? (
        <div className="space-y-2 rounded-xl border border-border p-3">
          <p className="font-medium text-sm">ຊຳລະເງິນ</p>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant={method === "cash" ? "default" : "outline"}
              onClick={() => setMethod("cash")}
            >
              ເງິນສົດ
            </Button>
            <Button
              type="button"
              size="sm"
              variant={method === "transfer" ? "default" : "outline"}
              onClick={() => setMethod("transfer")}
            >
              ໂອນ
            </Button>
          </div>
          {method === "transfer" ? (
            <div className="space-y-1">
              <Label>ເລກອ້າງອີງ</Label>
              <Input
                value={transferRef}
                onChange={(e) => setTransferRef(e.target.value)}
                placeholder="ເລກສະລິບ"
              />
            </div>
          ) : null}
          <Button
            type="button"
            size="lg"
            className="h-12 w-full text-base"
            disabled={
              checkoutMut.isPending ||
              (method === "transfer" && !transferRef.trim())
            }
            onClick={() => void checkout()}
          >
            ຊຳລະ {formatWorkshopMoney(quote.grandTotal, quote.currency)}
          </Button>
        </div>
      ) : null}

      {!paid && approved && job.status !== "completed" ? (
        <p className="text-muted-foreground text-xs">
          ອະນຸມັດແລ້ວ — ປ່ຽນສະຖານະເປັນ “ສຳເລັດ” ກ່ອນຊຳລະ
        </p>
      ) : null}
    </div>
  );
}
